import React, { useState } from "react";
import { Check, Camera, Clock, Users, Image as ImageIcon, FolderOpen, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function LoffertaWedding() {
  const [isWeddingDay, setIsWeddingDay] = useState(true);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const toggleMode = () => setIsWeddingDay(!isWeddingDay);
  const currentPackage = isWeddingDay ? {
    title: "The big day",
    subtitle: "Welcome Party",
    description: "An unforgettable experience during the wedding reception. Our artisan photographers entertain guests with portraits printed on the spot, creating a tangible and immediate memory of your celebration.",
    features: [{
      icon: Clock,
      text: "Up to 4 hours of wedding coverage"
    }, {
      icon: Users,
      text: "Two specialized operators"
    }, {
      icon: Camera,
      text: "'Minutera' camera with photography cart or trike"
    }, {
      icon: FolderOpen,
      text: "Delivery of prints in personalized folders."
    }, {
      icon: Star,
      text: "Direct interaction with guests during the developing process."
    }],
    priceLabel: "From",
    cta: "Request Availability"
  } : {
    title: "Welcome  Party",
    subtitle: "The Perfect Atmosphere to Begin ",
    description: "The ideal ice-breaker for your arrival event. A dynamic and engaging service that welcomes guests with their first handcrafted memory of your wedding weekend.",
    features: [{
      icon: Clock,
      text: "Up to 3 hours of welcome party coverage"
    }, {
      icon: Users,
      text: "Two specialized operators"
    }, {
      icon: Camera,
      text: "'Minutera' camera with photography cart or trike"
    }, {
      icon: FolderOpen,
      text: "Delivery of prints in personalized folders."
    }, {
      icon: Star,
      text: "Direct interaction with guests during the developing process."
    }],
    priceLabel: "The ideal solution for",
    cta: "Request Availability"
  };
  return <section id="lofferta-wedding" ref={ref} className="dark bg-background text-foreground py-20 px-4 md:px-8 overflow-hidden transition-opacity duration-700 opacity-100 translate-y-0">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight text-primary">
            The Wedding Experience
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-light">Discover our specialized services designed to create an unforgettable atmosphere and timeless keepsakes for you and your guests.</p>
        </div>

        <div className="flex justify-center items-center gap-6 mb-12">
          <span className={`text-sm md:text-base font-medium transition-colors ${!isWeddingDay ? "text-primary" : "text-muted-foreground"}`}>
            Welcome Party
          </span>
          <Switch checked={isWeddingDay} onCheckedChange={setIsWeddingDay} className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-primary/50" aria-label="Toggle between Welcome Party and Wedding Day" />
          <span className={`text-sm md:text-base font-medium transition-colors ${isWeddingDay ? "text-primary" : "text-muted-foreground"}`}>
            Wedding Day
          </span>
        </div>

        <div className="relative max-w-4xl mx-auto perspective-1000">
          <div className="absolute -top-8 left-0 w-48 h-10 bg-card border-t border-l border-r border-border rounded-t-xl z-0 hidden md:block">
            <div className="w-full h-full flex items-center justify-center text-xs font-mono text-muted-foreground uppercase tracking-widest">
              Dossier {isWeddingDay ? "N.01" : "N.02"}
            </div>
          </div>

          <Card className="relative z-10 border-border bg-card shadow-2xl overflow-hidden min-h-[600px] flex flex-col md:flex-row transition-all duration-500 ease-in-out">
            <div className="w-full md:w-2/5 bg-muted/30 relative border-b md:border-b-0 md:border-r border-border">
              <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562462/img/vintage_camera_wedding.png)] bg-cover bg-center opacity-60 mix-blend-overlay" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/90 md:to-transparent md:bg-gradient-to-r" />
              
              <div className="relative h-full p-8 flex flex-col justify-between z-10">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider border border-primary/20">
                    {isWeddingDay ? "Premium" : "Smart"}
                  </div>
                  <h3 className="text-3xl font-serif text-foreground leading-tight">{currentPackage.title}</h3>
                  <p className="text-muted-foreground text-sm italic">The Perfect Atmosphere to Begin</p>
                </div>

                <div className="mt-8 md:mt-0">
                  <div className="w-24 h-24 border border-primary/30 rounded-lg flex items-center justify-center mb-4 bg-background/50 backdrop-blur-sm">
                    <Camera className="w-10 h-10 text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    Ref: {isWeddingDay ? "WED-FULL-24" : "WEL-LITE-24"}
                    <br />
                    Status: Disponibile
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full md:w-3/5 p-8 flex flex-col">
              <CardHeader className="p-0 mb-6">
                <CardTitle className="text-xl font-sans font-light text-foreground/90">Service Details</CardTitle>
                <CardDescription className="mt-2 text-base">{currentPackage.description}</CardDescription>
              </CardHeader>

              <CardContent className="p-0 flex-grow">
                <ul className="space-y-4">
                  {currentPackage.features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3 group">
                      <div className="mt-1 min-w-5 min-h-5 rounded-lg border border-primary/30 flex items-center justify-center bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        {feature.icon && <feature.icon className="w-3 h-3 text-primary" />}
                      </div>
                      <div className="flex-1">
                        <span className="text-foreground/80 text-sm md:text-base font-light">
                          {feature.text}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </CardContent>

              <CardFooter className="p-0 mt-8 pt-6 border-t border-border flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">From</p>
                  <p className="text-2xl font-serif text-primary">Upon Request</p>
                </div>
                <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
                  <Link to="/ContactPage">Request Availability</Link>
                </Button>
              </CardFooter>
            </div>
          </Card>
          
          <div className="absolute top-2 left-2 w-full h-full border border-border rounded-xl -z-10 bg-card/50 translate-x-2 translate-y-2" />
          <div className="absolute top-4 left-4 w-full h-full border border-border/50 rounded-xl -z-20 bg-card/20 translate-x-4 translate-y-4" />
        </div>
      </div>
    </section>;
}